import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from '../lib/routerCompat';
import { useToast } from '../ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/db';
import { validateBrief, ACCEPTED_BRIEF_TYPE, MAX_BRIEF_LABEL } from '../lib/campaignBrief';
import { STAT_PLACEHOLDER } from '../lib/stats';
import {
  OPEN_CAMPAIGN_STATUS,
  CAMPAIGN_GOALS,
  CAMPAIGN_DELIVERABLES,
  CAMPAIGN_CONTENT_STYLES,
  UNSPECIFIED_BRIEF_FIELD,
} from '../types';
import type {
  Application,
  ApplicationStatus,
  Campaign,
  CampaignContentStyle,
  CampaignDeliverable,
  CampaignGoal,
  CampaignRow,
  CampaignStatus,
} from '../types';

/**
 * A campaign as held in view state: the app shape, still carrying the raw
 * brief_path/brief_name columns until hydration shapes them into `brief`.
 */
type CampaignItem = Omit<Campaign, 'brief'> & {
  /** Rendered brief; BriefDraft also admits unsaved local drafts (null path). */
  brief?: BriefDraft | null;
} & Partial<Pick<CampaignRow, 'brief_path' | 'brief_name'>>;

/**
 * A campaign brief being edited in the form. Mirrors {@link CampaignBrief} but
 * allows `path` to be `null` for an in-memory object URL awaiting upload.
 */
interface BriefDraft {
  /** Display filename, e.g. "brief.pdf". */
  name: string;
  /** Object URL or short-lived signed download URL; null when unavailable. */
  url?: string | null;
  /** Storage object path, or null for an unsaved local object URL. */
  path?: string | null;
}

// The KPI strip used to be four fixed numbers — '3' active campaigns,
// '$2,750' spent, '18' completed deals, '6.2%' engagement — rendered as the
// brand's own history. A brand with one campaign read "3 Active Campaigns" in
// production, which is how PIT-84 was found.
//
// The three that remain are computed from what the brand actually owns. Spend,
// completed deals and average engagement are gone rather than badged: a
// sample-data marker still leaves an invented figure on the screen, and
// "18 Completed Deals" is social proof about the platform's track record, not
// just a placeholder. PitchVault is pre-launch with no payments model, so there
// is nothing true to put there yet — they come back when there is.

/**
 * Props for {@link OptionGroup}, a single-select grid of labelled buttons.
 *
 * Generic over the option type so a grid built from a vocabulary hands that
 * vocabulary's type back on select, rather than a bare string the caller has to
 * re-narrow (PIT-83). Grids built from plain arrays still infer `string`.
 */
interface OptionGroupProps<T extends string> {
  /** Selectable option labels. */
  options: readonly T[];
  /** Grid column count: 2 renders `option-grid-2`, otherwise `option-grid`. */
  cols?: number;
  /** Currently selected option; '' selects none. */
  selected?: string;
  /** Invoked with the chosen option label. */
  onSelect: (value: T) => void;
}

function OptionGroup<T extends string>({
  options,
  cols = 3,
  selected,
  onSelect,
}: OptionGroupProps<T>): JSX.Element {
  return (
    <div className={cols === 2 ? 'option-grid-2' : 'option-grid'}>
      {options.map((o) => (
        <div
          key={o}
          className={`option-btn${selected === o ? ' sel' : ''}`}
          onClick={() => onSelect(o)}
        >
          {o}
        </div>
      ))}
    </div>
  );
}

/** The turnaround the form opens on, and falls back to for a row without one. */
const DEFAULT_TIMELINE = '3–5 Days';

/** How a decided application reads on the brand's review list. */
const APPLICATION_STATUS_LABEL: Record<ApplicationStatus, string> = {
  submitted: 'Awaiting your decision',
  accepted: 'Accepted',
  declined: 'Declined',
  withdrawn: 'Withdrawn by the creator',
};

export default function CampaignView() {
  const showToast = useToast();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [product, setProduct] = useState('');
  const [editingId, setEditingId] = useState<number | string | null>(null);
  // '' is a real state here, not a missing one: it is what a campaign created
  // before PIT-83 opens with, and it keeps the grid unselected so saving cannot
  // invent an answer the brand never gave.
  const [goal, setGoal] = useState<CampaignGoal | ''>(CAMPAIGN_GOALS[0]);
  const [deliverable, setDeliverable] = useState<CampaignDeliverable | ''>(
    CAMPAIGN_DELIVERABLES[0]
  );
  const [contentStyle, setContentStyle] = useState<CampaignContentStyle | ''>(
    CAMPAIGN_CONTENT_STYLES[0]
  );
  const [budget, setBudget] = useState('Open');
  const [timeline, setTimeline] = useState(DEFAULT_TIMELINE);
  const [budgetAmount, setBudgetAmount] = useState('');
  const [budgetError, setBudgetError] = useState(false);
  // Campaign brief PDF shown on the card: { name, url, path }. `path` is the
  // Supabase Storage key when persisted, or null for a demo-mode object URL.
  const [brief, setBrief] = useState<BriefDraft | null>(null);
  // A freshly-selected File awaiting upload on save (null when unchanged).
  const [briefFile, setBriefFile] = useState<File | null>(null);
  // Storage path of a brief the user replaced/removed, deleted on save.
  const [briefRemovedPath, setBriefRemovedPath] = useState<string | null>(null);
  const briefInputRef = useRef<HTMLInputElement>(null);

  // Applications on this brand's campaigns, grouped by campaign (PIT-79), and
  // which card has its applicant list open.
  const [applications, setApplications] = useState<Application[]>([]);
  // Whether the applications call actually came back. It is allowed to fail
  // without taking the page down, so an empty list means "none yet" only once
  // this is true — otherwise the count is unknown, not zero.
  const [applicationsLoaded, setApplicationsLoaded] = useState(false);
  const [openApplicants, setOpenApplicants] = useState<number | null>(null);
  const [decidingId, setDecidingId] = useState<number | null>(null);
  // Which campaign has a status write in flight, so a second click cannot race
  // the first — the same guard decidingId provides for accept/decline.
  const [statusChangingId, setStatusChangingId] = useState<number | string | null>(null);

  useEffect(() => {
    let active = true;
    db.getCampaigns().then(async (data) => {
      // Persisted rows carry brief_path/brief_name; mint short-lived signed
      // URLs so the private-bucket PDFs render as download links. Demo rows
      // (and freshly-created local ones) already carry a shaped `brief`.
      const hydrated = await Promise.all(
        data.map(async (c) => {
          if (c.brief || !c.brief_path) return c;
          const url = await db.getBriefUrl(c.brief_path);
          return {
            ...c,
            brief: { name: c.brief_name || 'brief.pdf', url, path: c.brief_path },
          };
        })
      );
      if (!active) return;
      setCampaigns(hydrated);
      setLoading(false);

      // Who applied, for every campaign at once rather than per card (PIT-79).
      const ids = hydrated.map((c) => Number(c.id)).filter((id) => Number.isInteger(id) && id > 0);
      db.getApplicationsForCampaigns(ids)
        .then((rows) => {
          if (!active) return;
          setApplications(rows);
          setApplicationsLoaded(true);
        })
        .catch(() => {
          // The campaign list stays usable; applicants simply don't load, and
          // the counts above them stay unknown rather than reading zero.
        });
    });
    return () => {
      active = false;
    };
  }, []);

  // Every figure here is derived from rows this brand owns — RLS decides what
  // getCampaigns and getApplicationsForCampaigns return, so there is nothing to
  // scope again. Unknown reads as a dash rather than 0; see STAT_PLACEHOLDER.
  const kpis = [
    {
      label: 'Active Campaigns',
      value: loading
        ? STAT_PLACEHOLDER
        : String(campaigns.filter((c) => c.status === OPEN_CAMPAIGN_STATUS).length),
    },
    {
      label: 'Applications',
      value: applicationsLoaded ? String(applications.length) : STAT_PLACEHOLDER,
    },
    {
      // The one a brand can act on, which is the point of showing it.
      label: 'Awaiting Review',
      value: applicationsLoaded
        ? String(applications.filter((a) => a.status === 'submitted').length)
        : STAT_PLACEHOLDER,
    },
  ];

  // Applications grouped by the campaign they were sent to.
  const applicantsByCampaign = applications.reduce<Record<number, Application[]>>((acc, a) => {
    acc[a.campaign_id] = [...(acc[a.campaign_id] ?? []), a];
    return acc;
  }, {});

  // Accept or decline. RLS decides whether the transition is allowed at all —
  // it refuses by returning no row rather than erroring, so a null result means
  // "not allowed", most likely because someone already decided this one.
  const decideApplication = async (id: number, status: ApplicationStatus): Promise<void> => {
    if (decidingId !== null) return;
    setDecidingId(id);
    try {
      const updated = await db.setApplicationStatus(id, status);
      if (!updated && !db.demoMode) {
        showToast('That application has already been decided — reload to see it');
        return;
      }
      setApplications((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));

      // Accepting opens the conversation (PIT-81). Deliberately after the
      // status update and outside its success path: the acceptance is the
      // decision that matters and has already been recorded, so a thread that
      // fails to open must not make the brand think the accept failed too.
      // The RPC is idempotent, so opening it later — on a retry, or from the
      // next accept — lands on the same conversation rather than a second one.
      if (status === 'accepted' && !db.demoMode) {
        try {
          await db.openConversationForApplication(id);
        } catch {
          showToast('Accepted — but the conversation could not be opened yet');
          return;
        }
      }
      showToast(status === 'accepted' ? 'Application accepted' : 'Application declined');
    } catch {
      showToast('Could not update that application — please try again');
    } finally {
      setDecidingId(null);
    }
  };

  const handleBriefChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = validateBrief(file);
    if (!result.ok) {
      // BriefValidation narrows to { ok: false; error } here, so error is set.
      showToast(result.error);
      if (briefInputRef.current) briefInputRef.current.value = '';
      return;
    }
    setBrief((prev) => {
      // Replacing a stored brief? Mark its path for deletion on save. Only
      // object URLs (no path) are ours to revoke; signed URLs are not blobs.
      if (prev?.path) setBriefRemovedPath(prev.path);
      else if (prev?.url) URL.revokeObjectURL(prev.url);
      return { name: file.name, url: URL.createObjectURL(file), path: null };
    });
    setBriefFile(file);
  };

  const removeBrief = () => {
    setBrief((prev) => {
      if (prev?.path) setBriefRemovedPath(prev.path);
      else if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
    setBriefFile(null);
    if (briefInputRef.current) briefInputRef.current.value = '';
  };

  // Reconcile brief changes against Supabase Storage for a saved campaign and
  // return the brief to render: a brief object, null (explicitly removed), or
  // undefined (no change / failure — keep whatever the campaign already has).
  // Demo mode keeps the in-memory object URL.
  const persistBrief = async (
    campaignId: number | string | undefined,
    ownerId: string | undefined
  ): Promise<BriefDraft | null | undefined> => {
    if (db.demoMode) return brief;
    try {
      if (briefFile) {
        // Upload the new file BEFORE deleting the replaced one, so a failed
        // upload can never leave the campaign with no brief at all.
        const stored = await db.uploadBrief(ownerId, campaignId, briefFile);
        if (!stored) return undefined; // missing owner/campaign — keep existing
        await db.updateCampaign(campaignId, {
          brief_path: stored.path,
          brief_name: stored.name,
        });
        if (briefRemovedPath && briefRemovedPath !== stored.path) {
          await db.removeBrief(briefRemovedPath);
        }
        const url = await db.getBriefUrl(stored.path);
        return { name: stored.name, url, path: stored.path };
      }
      if (briefRemovedPath) {
        await db.removeBrief(briefRemovedPath);
        await db.updateCampaign(campaignId, { brief_path: null, brief_name: null });
        return null;
      }
      return brief;
    } catch {
      showToast('Brief upload failed — please try again');
      return undefined; // preserve any existing brief on failure
    }
  };

  const handleSubmit = async () => {
    if (budget === 'Fixed' && (parseFloat(budgetAmount) < 250 || !budgetAmount)) {
      setBudgetError(true);
      showToast('Minimum campaign budget is $250');
      return;
    }
    setBudgetError(false);

    const amount = parseFloat(budgetAmount);
    // Editable fields only — status/applied/selected must survive edits.
    //
    // goal/deliverable/content_style were collected by the form and thrown away
    // on every submit until PIT-83. They are the answer to "what am I being
    // asked to make", which is what a creator needs most before applying.
    const fields = {
      title: name.trim() || 'Untitled Campaign',
      product: product.trim() || null,
      budget: amount > 0 ? `$${amount.toLocaleString()}` : 'Open',
      deadline: timeline,
      // Empty means the brand left a legacy campaign's blank grid alone. Null
      // keeps it "Not specified" rather than promoting the first option to an
      // answer they never gave.
      goal: goal || null,
      deliverable: deliverable || null,
      content_style: contentStyle || null,
    };

    const userId = session?.user?.id;

    if (editingId !== null) {
      await db.updateCampaign(editingId, fields);
      const finalBrief = await persistBrief(editingId, userId);
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === editingId
            ? { ...c, ...fields, brief: finalBrief === undefined ? c.brief : finalBrief }
            : c
        )
      );
      showToast('Campaign updated');
    } else {
      if (!db.demoMode && !userId) {
        showToast('Still connecting — try again in a second');
        return;
      }
      const row = { ...fields, status: 'Active' as const, applied: 0, selected: 0 };
      const created = await db.createCampaign(userId, row);
      if (!db.demoMode && !created) {
        showToast('Could not create the campaign — please try again');
        return;
      }
      const finalBrief = await persistBrief(created?.id, userId);
      setCampaigns((prev) => [
        { ...(created ?? { ...row, id: `local-${prev.length}` }), brief: finalBrief },
        ...prev,
      ]);
      showToast('Brief submitted — creators notified');
    }
    setShowForm(false);
    setEditingId(null);
    setName('');
    setProduct('');
    setBudgetAmount('');
    // Back to the defaults the grids show on a fresh brief. Without this, a
    // legacy campaign edited beforehand would leave its blank selections behind
    // for the next campaign created.
    setGoal(CAMPAIGN_GOALS[0]);
    setDeliverable(CAMPAIGN_DELIVERABLES[0]);
    setContentStyle(CAMPAIGN_CONTENT_STYLES[0]);
    setTimeline(DEFAULT_TIMELINE);
    setBrief(null);
    setBriefFile(null);
    setBriefRemovedPath(null);
    if (briefInputRef.current) briefInputRef.current.value = '';
  };

  const startEdit = (c: CampaignItem) => {
    setEditingId(c.id);
    setName(c.title ?? '');
    setProduct(c.product ?? '');
    const numeric = parseFloat(String(c.budget ?? '').replace(/[^0-9.]/g, ''));
    setBudgetAmount(numeric > 0 ? String(numeric) : '');
    setBudget(numeric > 0 ? 'Fixed' : 'Open');
    // Load what the campaign actually holds. These were never loaded before, so
    // opening Edit and saving quietly rewrote the timeline to the form default
    // and (once stored) would have done the same to the brief fields.
    //
    // A campaign from before PIT-83 has no answer for the three brief fields,
    // and '' matches no option, so its grids open with nothing selected. The
    // brand can choose — but simply saving will not put words in their mouth.
    setGoal(c.goal ?? '');
    setDeliverable(c.deliverable ?? '');
    setContentStyle(c.content_style ?? '');
    setTimeline(c.deadline || DEFAULT_TIMELINE);
    setBrief(c.brief ?? null);
    setBriefFile(null);
    setBriefRemovedPath(null);
    if (briefInputRef.current) briefInputRef.current.value = '';
    setShowForm(true);
  };

  // Pause and resume are the same write in opposite directions (PIT-85).
  //
  // Resume targets Active and only Active. 'In Progress' is a statement that
  // work has started, which is something the app should conclude when a creator
  // is picked — not something a brand asserts by pressing a button here.
  const changeCampaignStatus = async (
    id: number | string,
    status: CampaignStatus
  ): Promise<void> => {
    if (statusChangingId !== null) return;
    setStatusChangingId(id);
    const previous = campaigns.find((c) => c.id === id)?.status;
    setCampaigns((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    showToast(status === OPEN_CAMPAIGN_STATUS ? 'Campaign resumed' : 'Campaign paused');
    try {
      const updated = await db.setCampaignStatus(id, status);
      // Demo mode persists nothing and returns null by design; only a real
      // backend answering with no row means the write was refused.
      if (!updated && !db.demoMode) throw new Error('refused');
    } catch {
      // Put the card back. This status is what decides whether creators can see
      // the campaign at all, so a card left claiming "Paused" after a failed
      // write would tell the brand their campaign is hidden when it is live.
      //
      // Only undo what this attempt did, and only if it is still on screen: a
      // late failure must not overwrite a newer value with a stale one. The
      // guard above already prevents that from the UI, so this is the belt to
      // its braces.
      if (previous) {
        setCampaigns((prev) =>
          prev.map((c) => (c.id === id && c.status === status ? { ...c, status: previous } : c))
        );
      }
      showToast('Could not update that campaign — please try again');
    } finally {
      setStatusChangingId(null);
    }
  };

  if (loading)
    return (
      <div
        className="view"
        style={{
          textAlign: 'center',
          padding: '60px 0',
          fontFamily: 'var(--cinzel)',
          color: 'var(--text-muted)',
          letterSpacing: 3,
        }}
      >
        LOADING...
      </div>
    );

  return (
    <div className="view">
      <div className="section-header">Brands Dashboard</div>
      <div className="section-sub">Manage campaigns, track spend, and discover creators.</div>

      <div className="quick-action-bar">
        <button className="btn-gold qab-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? '✕ Close' : '+ Create Campaign'}
        </button>
        <button className="btn-outline qab-btn" onClick={() => navigate({ to: '/feed' })}>
          Browse Creators
        </button>
        <button className="btn-outline qab-btn" onClick={() => setShowForm(false)}>
          Active Campaigns
        </button>
        <button
          className="btn-outline qab-btn"
          onClick={() => navigate({ to: '/feed', search: { saved: true } })}
        >
          Saved Creators
        </button>
      </div>

      <div className="kpi-strip">
        {kpis.map((k) => (
          <div className="kpi-card" key={k.label}>
            <div className="kpi-value">{k.value}</div>
            <div className="kpi-label">{k.label}</div>
          </div>
        ))}
      </div>

      {!showForm && (
        <div>
          <div className="section-label-row">Active Campaigns</div>
          <div className="campaign-cards">
            {campaigns.map((c) => (
              <div className="campaign-card" key={c.id}>
                <div className="campaign-card-header">
                  <span className="campaign-card-title">{c.title}</span>
                  <span
                    className={`campaign-status-badge status-${c.status.toLowerCase().replace(' ', '-')}`}
                  >
                    {c.status}
                  </span>
                </div>
                <div className="campaign-card-meta">
                  <span>
                    Budget: <strong>{c.budget}</strong>
                  </span>
                  <span>
                    {/* The real count, from applications. `campaigns.applied` is
                        a counter nothing has ever incremented — see PIT-84. */}
                    Applied: <strong>{(applicantsByCampaign[Number(c.id)] ?? []).length}</strong>
                  </span>
                  <span>
                    Selected: <strong>{c.selected}</strong>
                  </span>
                  <span>
                    {/* A turnaround label, not a date — see the PIT-83 migration. */}
                    Timeline: <strong>{c.deadline || UNSPECIFIED_BRIEF_FIELD}</strong>
                  </span>
                  <span>
                    Deliverable: <strong>{c.deliverable || UNSPECIFIED_BRIEF_FIELD}</strong>
                  </span>
                  <span>
                    Objective: <strong>{c.goal || UNSPECIFIED_BRIEF_FIELD}</strong>
                  </span>
                  <span>
                    Style: <strong>{c.content_style || UNSPECIFIED_BRIEF_FIELD}</strong>
                  </span>
                </div>
                {c.brief?.url && (
                  <a
                    className="btn-gold campaign-brief-download"
                    href={c.brief.url}
                    download={c.brief.name}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      padding: '6px 14px',
                      marginBottom: 10,
                      textDecoration: 'none',
                    }}
                  >
                    ⬇ Download Brief (PDF)
                  </a>
                )}
                <div className="campaign-card-actions">
                  <button
                    className="btn-outline"
                    style={{ fontSize: 12, padding: '5px 14px' }}
                    onClick={() => startEdit(c)}
                  >
                    Edit
                  </button>
                  {/* A paused campaign is invisible to creators, so the way
                      back is the more important control to offer here. */}
                  {c.status === 'Paused' ? (
                    <button
                      className="btn-ghost"
                      style={{ fontSize: 12, padding: '5px 14px' }}
                      onClick={() => changeCampaignStatus(c.id, OPEN_CAMPAIGN_STATUS)}
                      disabled={statusChangingId !== null}
                    >
                      Resume
                    </button>
                  ) : (
                    <button
                      className="btn-ghost"
                      style={{ fontSize: 12, padding: '5px 14px' }}
                      onClick={() => changeCampaignStatus(c.id, 'Paused')}
                      disabled={statusChangingId !== null}
                    >
                      Pause
                    </button>
                  )}
                  <button
                    className="btn-outline"
                    style={{ fontSize: 12, padding: '5px 14px' }}
                    onClick={() =>
                      setOpenApplicants((prev) => (prev === Number(c.id) ? null : Number(c.id)))
                    }
                  >
                    {openApplicants === Number(c.id) ? 'Hide applicants' : 'View applicants'}
                  </button>
                </div>

                {openApplicants === Number(c.id) && (
                  <div style={{ marginTop: 14 }}>
                    {(applicantsByCampaign[Number(c.id)] ?? []).length === 0 ? (
                      /* Pre-launch this is the expected state; it says so
                         rather than showing invented applicants. */
                      <div className="section-sub">
                        No applications yet. Creators see this campaign in their feed while it is
                        active.
                      </div>
                    ) : (
                      (applicantsByCampaign[Number(c.id)] ?? []).map((a) => (
                        <div className="panel" key={a.id} style={{ marginBottom: 10 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              flexWrap: 'wrap',
                              marginBottom: 8,
                            }}
                          >
                            <span className="creator-name" style={{ fontSize: 18 }}>
                              {/* An applicant whose profile could not be read
                                  still has an application worth showing. The
                                  PIT-79 policy makes this rare, not impossible
                                  — a deleted profile still leaves the row. */}
                              {a.creator?.name ?? 'Creator profile unavailable'}
                            </span>
                            {a.creator?.handle && (
                              <span className="creator-handle">{a.creator.handle}</span>
                            )}
                            {a.creator?.niche && (
                              <span className="niche-tag primary">{a.creator.niche}</span>
                            )}
                            {a.creator?.rate && <span className="niche-tag">{a.creator.rate}</span>}
                          </div>

                          <p className="my-pitch-text" style={{ marginBottom: 10 }}>
                            {a.pitch_text}
                          </p>

                          {a.status === 'submitted' ? (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                className="btn-approve approve"
                                onClick={() => decideApplication(a.id, 'accepted')}
                                disabled={decidingId !== null}
                              >
                                Accept
                              </button>
                              <button
                                className="btn-approve decline"
                                onClick={() => decideApplication(a.id, 'declined')}
                                disabled={decidingId !== null}
                              >
                                Decline
                              </button>
                            </div>
                          ) : (
                            <div className="escrow-badge badge-pending" role="status">
                              {APPLICATION_STATUS_LABEL[a.status]}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="panel">
          <div className="form-section">
            <div className="form-section-label">Campaign Intelligence</div>
            <div style={{ marginBottom: 10 }}>
              <label className="form-label">Campaign name</label>
              <input
                className="form-input"
                placeholder="e.g. Summer Launch Series"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label className="form-label">Product / Service</label>
              <input
                className="form-input"
                placeholder="Describe your product"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Objective</label>
              <OptionGroup options={CAMPAIGN_GOALS} selected={goal} onSelect={setGoal} />
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-label">Deliverables</div>
            <OptionGroup
              options={CAMPAIGN_DELIVERABLES}
              cols={2}
              selected={deliverable}
              onSelect={setDeliverable}
            />
          </div>

          <div className="form-section">
            <div className="form-section-label">Content Style</div>
            <OptionGroup
              options={CAMPAIGN_CONTENT_STYLES}
              cols={2}
              selected={contentStyle}
              onSelect={setContentStyle}
            />
          </div>

          <div className="form-section">
            <div className="form-section-label">Investment</div>
            <OptionGroup
              options={['Fixed', 'Open', 'Get Quote']}
              selected={budget}
              onSelect={(v) => {
                setBudget(v);
                setBudgetError(false);
              }}
            />
            {budget === 'Fixed' && (
              <div style={{ marginTop: 8 }}>
                <input
                  className={`form-input${budgetError ? ' input-error' : ''}`}
                  placeholder="$ Amount (min. $250)"
                  value={budgetAmount}
                  onChange={(e) => {
                    setBudgetAmount(e.target.value);
                    setBudgetError(false);
                  }}
                />
                {budgetError && (
                  <div
                    style={{
                      fontSize: 13.5,
                      color: '#e06060',
                      fontFamily: 'var(--cinzel)',
                      letterSpacing: 1,
                      marginTop: 6,
                    }}
                  >
                    Minimum campaign budget is $250
                  </div>
                )}
              </div>
            )}
            {budget !== 'Fixed' && (
              <div style={{ marginTop: 8 }}>
                <input
                  className="form-input"
                  placeholder="$ Amount (optional)"
                  value={budgetAmount}
                  onChange={(e) => setBudgetAmount(e.target.value)}
                />
              </div>
            )}
            <div
              style={{
                fontSize: 13.5,
                color: 'var(--text-muted)',
                fontFamily: 'var(--cinzel)',
                letterSpacing: 1,
                marginTop: 8,
              }}
            >
              Platform minimum: $250 per campaign
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-label">Timeline</div>
            <OptionGroup
              options={['ASAP', '3–5 Days', 'Custom']}
              selected={timeline}
              onSelect={setTimeline}
            />
          </div>

          <div className="form-section">
            <div className="form-section-label">Campaign Brief</div>
            <div
              style={{
                fontSize: 13.5,
                color: 'var(--text-muted)',
                fontFamily: 'var(--cinzel)',
                letterSpacing: 1,
                marginBottom: 8,
              }}
            >
              Optional PDF ({MAX_BRIEF_LABEL} max) — shared with creators reviewing this campaign.
            </div>
            <input
              ref={briefInputRef}
              type="file"
              accept={ACCEPTED_BRIEF_TYPE}
              aria-label="Upload campaign brief PDF"
              onChange={handleBriefChange}
            />
            {brief && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 13.5 }}>📄 {brief.name}</span>
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ fontSize: 12, padding: '3px 12px' }}
                  onClick={removeBrief}
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <button className="btn-submit-gold" onClick={handleSubmit}>
            {editingId !== null ? 'Save Campaign Changes' : 'Submit Commission Brief'}
          </button>
        </div>
      )}
    </div>
  );
}
